/**
 * GuestRequestsBlock — las peticiones del huésped que hay que atender hoy.
 *
 * Solo lo pendiente, cuatro como mucho. El catálogo de lo que el portal ofrece
 * de un toque no vive aquí: vive en Ajustes › Servicios y en el propio portal.
 * Hoy enseña trabajo, no un escaparate.
 *
 * El modelo importa más que la lista: una petición no es un mensaje suelto de
 * WhatsApp, es un registro con tipo, unidad, hora, estado y área responsable
 * (§7.2, §9.2). Por eso "toallas limpias" puede convertirse en una tarea de
 * limpieza y "reportar algo" en una incidencia de mantenimiento.
 */
import React, { useState } from "react";
import {
  TShirt, Broom, Basket, Car, Scooter, ForkKnife, Gift, Clock, Warning,
} from "@phosphor-icons/react";
import {
  REQUEST_STATUS_LABEL, REQUEST_STATUS_TONE, REQUEST_FLOW,
  AREA_LABEL, URGENCY_LABEL,
} from "@/data/todayV2Demo";
import { sortRequestsByUrgency } from "@/lib/todayV2Selectors";
import {
  TodayBlock, TodayCard, RowList, Row, Meta, StatusPill, QuickAction,
  EmptyState, ShowMore,
} from "@/pages/admin/pms/v2/V2Primitives";

const ICONS = { TShirt, Broom, Basket, Car, Scooter, ForkKnife, Gift, Clock, Warning };
const VISIBLE_BY_DEFAULT = 4;

export default function GuestRequestsBlock({
  requests, showGuestNames, onAdvanceRequest, onCompleteRequest,
}) {
  const [expanded, setExpanded] = useState(false);
  const open = sortRequestsByUrgency(
    requests.filter((r) => r.status !== "delivered" && r.status !== "cancelled"),
  );
  const visible = expanded ? open : open.slice(0, VISIBLE_BY_DEFAULT);
  const hasMore = open.length > visible.length;

  return (
    <TodayBlock
      id="today-requests"
      title="Peticiones"
      count={open.length}
      testId="today-requests"
    >
      {visible.length === 0 ? (
        <EmptyState testId="today-requests-empty">Sin peticiones pendientes.</EmptyState>
      ) : (
        <TodayCard padding="p-0">
          <RowList testId="today-requests-list">
            {visible.map((request) => (
              <RequestRow
                key={request.request_id}
                request={request}
                showGuestNames={showGuestNames}
                onAdvanceRequest={onAdvanceRequest}
                onCompleteRequest={onCompleteRequest}
              />
            ))}
          </RowList>
        </TodayCard>
      )}

      {(hasMore || expanded) && (
        <ShowMore
          expanded={expanded}
          onToggle={() => setExpanded((v) => !v)}
          moreLabel={`Ver las ${open.length} peticiones`}
          testId="today-requests-more"
        />
      )}
    </TodayBlock>
  );
}

function RequestRow({ request, showGuestNames, onAdvanceRequest, onCompleteRequest }) {
  const Icon = ICONS[request.icon] || Basket;
  const closed = request.status === "delivered" || request.status === "cancelled";
  const next = REQUEST_FLOW[REQUEST_FLOW.indexOf(request.status) + 1];

  return (
    <Row testId={`request-${request.request_id}`} className={closed ? "opacity-60" : ""}>
      <div className="flex items-start gap-2.5">
        <Icon size={15} className="mt-0.5 shrink-0 text-stone-400 dark:text-stone-500" />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-stone-900 dark:text-stone-100">
              {request.villa_id}
              <span className="text-stone-400"> · </span>
              {request.label}
              {request.quantity ? <span className="text-stone-400"> × {request.quantity}</span> : null}
            </span>
            <StatusPill tone={REQUEST_STATUS_TONE[request.status]} testId={`request-status-${request.request_id}`}>
              {REQUEST_STATUS_LABEL[request.status]}
            </StatusPill>
            {request.urgency && (
              <StatusPill tone={request.urgency === "high" ? "red" : "amber"} dot={false}>
                {URGENCY_LABEL[request.urgency]}
              </StatusPill>
            )}
            {request.issue_id && (
              <StatusPill tone="amber" dot={false} testId={`request-issue-${request.request_id}`}>
                {request.issue_id}
              </StatusPill>
            )}
          </div>

          <Meta
            items={[
              AREA_LABEL[request.area],
              showGuestNames ? request.guest_name : null,
              request.requested_at,
              request.scheduled_for ? `para ${request.scheduled_for}` : null,
              request.service_inactive ? "servicio desactivado en Ajustes" : null,
            ]}
            className="mt-0.5"
          />

          {request.description && (
            <div className="text-xs text-stone-600 dark:text-stone-300 mt-1">
              «{request.description}»
            </div>
          )}
        </div>

        {!closed && (
          <div className="shrink-0 flex items-center gap-1.5">
            {next && (
              <QuickAction
                label={REQUEST_STATUS_LABEL[next]}
                primary
                onClick={() => onAdvanceRequest(request.request_id)}
                testId={`request-advance-${request.request_id}`}
              />
            )}
            <QuickAction
              label="Completar"
              onClick={() => onCompleteRequest(request.request_id)}
              testId={`request-complete-${request.request_id}`}
            />
          </div>
        )}
      </div>
    </Row>
  );
}
